export const EU_LANGUAGES = {
  bg: { name: 'Български', nativeName: 'Bulgarian', country: 'BG' },
  cs: { name: 'Čeština', nativeName: 'Czech', country: 'CZ' },
  da: { name: 'Dansk', nativeName: 'Danish', country: 'DK' },
  de: { name: 'Deutsch', nativeName: 'German', country: 'DE' },
  el: { name: 'Ελληνικά', nativeName: 'Greek', country: 'GR' },
  en: { name: 'English', nativeName: 'English', country: 'GB' },
  es: { name: 'Español', nativeName: 'Spanish', country: 'ES' },
  et: { name: 'Eesti', nativeName: 'Estonian', country: 'EE' },
  fi: { name: 'Suomi', nativeName: 'Finnish', country: 'FI' },
  fr: { name: 'Français', nativeName: 'French', country: 'FR' },
  ga: { name: 'Gaeilge', nativeName: 'Irish', country: 'IE' },
  hr: { name: 'Hrvatski', nativeName: 'Croatian', country: 'HR' },
  hu: { name: 'Magyar', nativeName: 'Hungarian', country: 'HU' },
  it: { name: 'Italiano', nativeName: 'Italian', country: 'IT' },
  lt: { name: 'Lietuvių', nativeName: 'Lithuanian', country: 'LT' },
  lv: { name: 'Latviešu', nativeName: 'Latvian', country: 'LV' },
  mt: { name: 'Malti', nativeName: 'Maltese', country: 'MT' },
  nl: { name: 'Nederlands', nativeName: 'Dutch', country: 'NL' },
  pl: { name: 'Polski', nativeName: 'Polish', country: 'PL' },
  pt: { name: 'Português', nativeName: 'Portuguese', country: 'PT' },
  ro: { name: 'Română', nativeName: 'Romanian', country: 'RO' },
  sk: { name: 'Slovenčina', nativeName: 'Slovak', country: 'SK' },
  sl: { name: 'Slovenščina', nativeName: 'Slovenian', country: 'SI' },
  sv: { name: 'Svenska', nativeName: 'Swedish', country: 'SE' },
} as const;

export type LanguageCode = keyof typeof EU_LANGUAGES;

export const COUNTRY_TO_LANGUAGE: Record<string, LanguageCode> = {
  AT: 'de', BE: 'nl', BG: 'bg', CY: 'el', CZ: 'cs',
  DE: 'de', DK: 'da', EE: 'et', ES: 'es', FI: 'fi',
  FR: 'fr', GB: 'en', GR: 'el', HR: 'hr', HU: 'hu',
  IE: 'en', IT: 'it', LT: 'lt', LU: 'fr', LV: 'lv',
  MT: 'mt', NL: 'nl', PL: 'pl', PT: 'pt', RO: 'ro',
  SE: 'sv', SI: 'sl', SK: 'sk',
};

export const VAT_RATES: Record<string, number> = {
  AT: 20, BE: 21, BG: 20, CY: 19, CZ: 21,
  DE: 19, DK: 25, EE: 22, ES: 21, FI: 24,
  FR: 20, GR: 24, HR: 25, HU: 27, IE: 23,
  IT: 22, LT: 21, LU: 17, LV: 21, MT: 18,
  NL: 21, PL: 23, PT: 23, RO: 19, SE: 25,
  SI: 22, SK: 20,
};

export const COUNTRY_NAMES: Record<LanguageCode, Record<string, string>> = {
  it: {
    AT: 'Austria', BE: 'Belgio', BG: 'Bulgaria', CY: 'Cipro', CZ: 'Repubblica Ceca',
    DE: 'Germania', DK: 'Danimarca', EE: 'Estonia', ES: 'Spagna', FI: 'Finlandia',
    FR: 'Francia', GB: 'Regno Unito', GR: 'Grecia', HR: 'Croazia', HU: 'Ungheria',
    IE: 'Irlanda', IT: 'Italia', LT: 'Lituania', LU: 'Lussemburgo', LV: 'Lettonia',
    MT: 'Malta', NL: 'Paesi Bassi', PL: 'Polonia', PT: 'Portogallo', RO: 'Romania',
    SE: 'Svezia', SI: 'Slovenia', SK: 'Slovacchia',
  },
  en: {
    AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', CY: 'Cyprus', CZ: 'Czech Republic',
    DE: 'Germany', DK: 'Denmark', EE: 'Estonia', ES: 'Spain', FI: 'Finland',
    FR: 'France', GB: 'United Kingdom', GR: 'Greece', HR: 'Croatia', HU: 'Hungary',
    IE: 'Ireland', IT: 'Italy', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia',
    MT: 'Malta', NL: 'Netherlands', PL: 'Poland', PT: 'Portugal', RO: 'Romania',
    SE: 'Sweden', SI: 'Slovenia', SK: 'Slovakia',
  },
  de: {
    AT: 'Österreich', BE: 'Belgien', BG: 'Bulgarien', CY: 'Zypern', CZ: 'Tschechien',
    DE: 'Deutschland', DK: 'Dänemark', EE: 'Estland', ES: 'Spanien', FI: 'Finnland',
    FR: 'Frankreich', GB: 'Vereinigtes Königreich', GR: 'Griechenland', HR: 'Kroatien', HU: 'Ungarn',
    IE: 'Irland', IT: 'Italien', LT: 'Litauen', LU: 'Luxemburg', LV: 'Lettland',
    MT: 'Malta', NL: 'Niederlande', PL: 'Polen', PT: 'Portugal', RO: 'Rumänien',
    SE: 'Schweden', SI: 'Slowenien', SK: 'Slowakei',
  },
  fr: {
    AT: 'Autriche', BE: 'Belgique', BG: 'Bulgarie', CY: 'Chypre', CZ: 'République tchèque',
    DE: 'Allemagne', DK: 'Danemark', EE: 'Estonie', ES: 'Espagne', FI: 'Finlande',
    FR: 'France', GB: 'Royaume-Uni', GR: 'Grèce', HR: 'Croatie', HU: 'Hongrie',
    IE: 'Irlande', IT: 'Italie', LT: 'Lituanie', LU: 'Luxembourg', LV: 'Lettonie',
    MT: 'Malte', NL: 'Pays-Bas', PL: 'Pologne', PT: 'Portugal', RO: 'Roumanie',
    SE: 'Suède', SI: 'Slovénie', SK: 'Slovaquie',
  },
  es: {
    AT: 'Austria', BE: 'Bélgica', BG: 'Bulgaria', CY: 'Chipre', CZ: 'República Checa',
    DE: 'Alemania', DK: 'Dinamarca', EE: 'Estonia', ES: 'España', FI: 'Finlandia',
    FR: 'Francia', GB: 'Reino Unido', GR: 'Grecia', HR: 'Croacia', HU: 'Hungría',
    IE: 'Irlanda', IT: 'Italia', LT: 'Lituania', LU: 'Luxemburgo', LV: 'Letonia',
    MT: 'Malta', NL: 'Países Bajos', PL: 'Polonia', PT: 'Portugal', RO: 'Rumania',
    SE: 'Suecia', SI: 'Eslovenia', SK: 'Eslovaquia',
  },
  // Fallback to English for other languages
  bg: {}, cs: {}, da: {}, el: {}, et: {}, fi: {}, ga: {}, hr: {}, hu: {},
  lt: {}, lv: {}, mt: {}, nl: {}, pl: {}, pt: {}, ro: {}, sk: {}, sl: {}, sv: {},
};
